import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import Image from 'next/image'
import { useEffect } from 'react'
import {
    ChevronDown,
    Phone,
    Search,
    ShieldCheck,
    X
} from 'lucide-react'
import TextSizeToggle from '@/components/generals/text-size-toggle'
import WindowDiagram from '@/components/sections/window-diagram'

import { BUSINESS } from '@/content/business'
import { MAIN_NAV, SIMPLE_NAV } from '@/content/navigation'

interface MobileMenuProps {
    open: boolean
    onClose: () => void
    onSearch: () => void
}

const MobileMenu: FC<MobileMenuProps> = ({ open, onClose, onSearch }): ReactNode => {
    useEffect(() => {
        if (!open) return

        document.body.style.overflow = 'hidden'

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        window.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = ''
            window.removeEventListener('keydown', onKey)
        }
    }, [open, onClose])

    if (!open) return null

    return (
        <div
            id='mobile-menu'
            role='dialog'
            aria-modal='true'
            aria-label='Menu'
            className='fixed inset-0 z-[70] flex flex-col bg-[#101820] text-white animate-in fade-in slide-in-from-right-8 duration-300'
        >
            <div className='flex items-center justify-between border-b border-white/10 px-4 py-3'>
                <Image src='/images/acm/logo-dark.webp' alt='The ACM Pros Exteriors' width={194} height={128} className='h-12 w-auto' />

                <button
                    type='button'
                    onClick={onClose}
                    className='inline-flex min-h-12 items-center gap-2 rounded-lg border-2 border-white/25 px-4 font-bold text-white'
                >
                    <X className='size-5' aria-hidden='true' />
                    Close
                </button>
            </div>

            <div className='flex-1 overflow-y-auto px-4 pb-10 pt-4'>
                <div className='grid grid-cols-2 gap-3'>
                    <a
                        href={BUSINESS.phoneHref}
                        className='inline-flex min-h-14 items-center justify-center gap-2 rounded-lg bg-[#0074c8] px-3 text-lg font-bold text-white'
                    >
                        <Phone className='size-5' aria-hidden='true' />
                        Call now
                    </a>

                    <Link
                        href='/contact/'
                        onClick={onClose}
                        className='inline-flex min-h-14 items-center justify-center rounded-lg bg-white px-3 text-lg font-bold text-navy'
                    >
                        Free estimate
                    </Link>
                </div>

                <button
                    type='button'
                    onClick={onSearch}
                    className='mt-3 inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-lg border-2 border-white/25 text-lg font-bold text-white'
                >
                    <Search className='size-5' aria-hidden='true' />
                    Search the site
                </button>

                <nav aria-label='Mobile' className='mt-6'>
                    <ul className='border-t border-[#24384a]'>
                        {MAIN_NAV.map(group => (
                            <li key={group.label} className='border-b border-[#24384a]'>
                                <details className='group'>
                                    <summary className='flex min-h-16 cursor-pointer list-none items-center justify-between text-[1.4rem] font-semibold text-white [&::-webkit-details-marker]:hidden'>
                                        {group.label}
                                        <ChevronDown className='size-6 transition-transform group-open:rotate-180' aria-hidden='true' />
                                    </summary>

                                    <div className='pb-5'>
                                        {group.note && (
                                            <p className='mb-3 flex items-start gap-2 rounded-xl bg-[#fff4d6] px-4 py-3 text-base font-semibold text-navy'>
                                                <ShieldCheck className='mt-1 size-5 shrink-0 text-[#a66b00]' aria-hidden='true' />
                                                {group.note}
                                            </p>
                                        )}

                                        <Link href={group.feature.href} onClick={onClose} className='mb-2 block rounded-lg px-2 py-3 text-lg font-bold text-sky underline underline-offset-4'>
                                            {group.feature.cta}
                                        </Link>

                                        {group.columns.map(column => (
                                            <div key={column.title} className='mt-2'>
                                                <p className='px-2 pt-2 text-sm font-bold uppercase tracking-wider text-white/80'>
                                                    {column.title}
                                                </p>

                                                <ul className='grid sm:grid-cols-2 sm:gap-x-4'>
                                                    {column.links.map(link => (
                                                        <li key={link.href}>
                                                            <Link
                                                                href={link.href}
                                                                onClick={onClose}
                                                                className='flex min-h-14 items-center gap-3 rounded-lg px-2 py-2.5 hover:bg-white/5 focus-visible:bg-white/10'
                                                            >
                                                                {link.diagram && (
                                                                    <span className='flex size-11 shrink-0 items-center justify-center rounded-lg bg-white p-1'>
                                                                        <WindowDiagram diagram={link.diagram} />
                                                                    </span>
                                                                )}

                                                                <span className='min-w-0'>
                                                                    <span className='block text-lg font-semibold leading-snug text-white'>{link.label}</span>
                                                                    {link.description && <span className='mt-0.5 block text-sm leading-snug text-white/75'>{link.description}</span>}
                                                                </span>
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </details>
                            </li>
                        ))}

                        {[...SIMPLE_NAV, { label: "Reviews", href: '/reviews/' }].map(link => (
                            <li key={link.href} className='border-b border-[#24384a]'>
                                <Link
                                    href={link.href}
                                    onClick={onClose}
                                    className='flex min-h-16 items-center text-[1.4rem] font-semibold text-white'
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='mt-6 rounded-2xl bg-white/5 p-4'>
                    <TextSizeToggle />

                    <p className='mt-4 text-base text-white/85'>
                        <strong className='text-white'>Hours:</strong> {BUSINESS.hoursText}
                    </p>

                    <p className='mt-1 text-base text-white/85'>
                        {BUSINESS.licenseLabel}. Licensed and insured.
                    </p>
                </div>
            </div>
        </div>
    )
}

export default MobileMenu
